import { useEffect, useState } from 'react';
import { Form, type FormSubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { usePermission } from '@/hooks/usePermission.ts';
import { useAuth } from '@/hooks/useAuth';
import { config } from '@/config';
import OrgMspSection from '@/components/OrgMspSection';
import { useFeature } from '@/hooks/useCapabilities';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUsers } from '@/hooks/useUsers';
import { useTransferOwnership } from '@/hooks/useTransferOwnership';

interface FormInputs {
  name: string;
}

interface OrgResponse {
  id: string;
  name: string;
  slug: string;
  isMsp: boolean;
}

/**
 * Organization Admin → General: the organization's name, its owner, and MSP mode.
 * The rest of what used to share this page has a page of its own under `/admin`
 * (`SettingsPages.tsx`).
 */
export default function OrganizationSettings() {
  const { user } = useAuth();
  const [transferOpen, setTransferOpen] = useState(false);
  const [successorId, setSuccessorId] = useState('');
  const transferOwnership = useTransferOwnership();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Edit the organization of the ACTIVE context, not the user's home org: an
  // MSP user who switches context expects to manage the org they switched to.
  // `user.activeContext` refreshes when the context changes (the switcher
  // invalidates the current-user query), so this stays reactive to switches.
  const activeOrgId = user?.activeContext?.organizationId ?? user?.organizationId ?? null;
  const activeOrg =
    user?.organizations.find((o) => o.id === activeOrgId) ?? user?.organization ?? null;

  // Resolving the owner needs the roster: the organization carries an id, and a
  // name is what a reader can act on.
  const { data: members } = useUsers(activeOrgId);
  const owner = members?.find((m) => m.id === activeOrg?.ownerUserId);
  const ownerLabel = owner
    ? `${owner.firstName} ${owner.lastName}`.trim() || owner.email
    : 'Not recorded';
  const isOwner = Boolean(activeOrg?.ownerUserId && activeOrg.ownerUserId === user?.id);
  const activeOrgName = activeOrg?.name ?? '';

  // The MSP section is edition-gated only: it must still render when the org has
  // *not* opted in yet, which is exactly when someone needs to turn it on. (That
  // is why this is `useFeature('msp')` and not `useIsMsp()`.)
  const mspAvailable = useFeature('msp');
  // `PUT /api/organizations/{id}` authorizes against the organization, so a
  // division-scoped grant of the same name does not reach it.
  const canEditOrg = usePermission('org:update', 'organization');

  const {
    register,
    control,
    reset,
    formState: { errors },
  } = useForm<FormInputs>({ defaultValues: { name: activeOrgName } });

  // `defaultValues` is only read on mount, so re-seed the field when the active
  // org changes on a context switch — otherwise it keeps showing the prior org.
  useEffect(() => {
    reset({ name: activeOrgName });
  }, [activeOrgName, reset]);

  const updateOrganizationMutation = useMutation<OrgResponse, Error, FormInputs>({
    mutationFn: async (data) => {
      if (!activeOrgId) {
        throw new Error('No organization to update');
      }
      const response = await axios.patch<OrgResponse>(
        `${config.backendApi}/api/organizations/${activeOrgId}`,
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      void queryClient.invalidateQueries({ queryKey: ['workflow', 'onboarding'] });
      void navigate('/home');
    },
    onError: (error) => {
      console.error('Failed to update organization:', error);
    },
  });

  const onSubmit: FormSubmitHandler<FormInputs> = ({ data }) => {
    updateOrganizationMutation.mutate(data);
  };

  return (
    <PageContainer width="narrow" className="space-y-5 md:space-y-6">
      <PageHeader
        title="General"
        subtitle={
          activeOrgName
            ? `Managing ${activeOrgName}`
            : 'Update your organization details'
        }
      />

      <section className="bg-card border border-border rounded-lg">
        <Form
          control={control}
          encType="application/json"
          onSubmit={onSubmit}
          aria-label="Update Organization"
          className="p-5 space-y-4"
        >
          <div className="space-y-1.5">
            <Label>Owner</Label>
            <p className="text-sm">
              {ownerLabel}
              {isOwner ? (
                <span className="text-xs text-muted-foreground"> — that&apos;s you</span>
              ) : null}
            </p>
            <p className="text-xs text-muted-foreground">
              The owner answers for this organization&apos;s billing and can administer
              all of it. {isOwner ? 'Hand it over if someone else should be responsible.' : null}
            </p>
            {isOwner ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-1"
                onClick={() => { setTransferOpen(true); }}
              >
                Transfer ownership
              </Button>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="organizationName">Organization name</Label>
            <Input
              id="organizationName"
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? 'org-name-error' : undefined}
              placeholder="My Company"
              {...register('name', {
                required: 'Organization name is required',
                minLength: { value: 2, message: 'Name must be at least 2 characters' },
              })}
            />
            {errors.name ? (
              <p id="org-name-error" className="text-xs text-destructive" role="alert">
                {errors.name.message}
              </p>
            ) : null}
          </div>

          {updateOrganizationMutation.isError ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {updateOrganizationMutation.error.message ||
                  'An unknown error has occurred. Please try again.'}
              </AlertDescription>
            </Alert>
          ) : null}

          {updateOrganizationMutation.isSuccess ? (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>Organization updated successfully.</AlertDescription>
            </Alert>
          ) : null}

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => { void navigate('/home'); }}
              disabled={updateOrganizationMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={updateOrganizationMutation.isPending}>
              {updateOrganizationMutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </Form>
      </section>

      {mspAvailable ? (
        <section className="bg-card border border-border rounded-lg p-5">
          <OrgMspSection
            organizationId={activeOrgId}
            isMsp={activeOrg?.isMsp ?? false}
            canEdit={canEditOrg}
          />
        </section>
      ) : null}

      <Dialog
        open={transferOpen}
        onOpenChange={(next) => {
          if (!transferOwnership.isPending) {
            setTransferOpen(next);
            if (!next) setSuccessorId('');
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Transfer ownership</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            The new owner becomes answerable for this organization and gains full
            administrative authority over it. You keep your own access.
          </p>

          <div className="space-y-1.5">
            <Label htmlFor="os-successor">New owner</Label>
            <Select value={successorId} onValueChange={setSuccessorId}>
              <SelectTrigger id="os-successor">
                <SelectValue placeholder="Select a member…" />
              </SelectTrigger>
              <SelectContent>
                {(members ?? [])
                  .filter((m) => m.id !== activeOrg?.ownerUserId)
                  .map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {`${m.firstName} ${m.lastName}`.trim() || m.email}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {transferOwnership.isError ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {transferOwnership.error.response?.data.description ??
                  transferOwnership.error.response?.data.error ??
                  'Could not transfer ownership.'}
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => { setTransferOpen(false); setSuccessorId(''); }}
              disabled={transferOwnership.isPending}
            >
              Cancel
            </Button>
            <Button
              disabled={!successorId || transferOwnership.isPending}
              onClick={() => {
                if (!activeOrgId || !successorId) return;
                transferOwnership.mutate(
                  { organizationId: activeOrgId, newOwnerUserId: successorId },
                  { onSuccess: () => { setTransferOpen(false); setSuccessorId(''); } },
                );
              }}
            >
              {transferOwnership.isPending ? 'Transferring…' : 'Transfer ownership'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </PageContainer>
  );
}
